import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaFilter, FaSync, FaCheck, FaTimes, FaEnvelope, FaHome, FaCalendarAlt, FaUserAlt, FaPhoneAlt, FaCommentAlt, FaMapMarkerAlt, FaTrashAlt, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { updateEnquiryStatus, deleteEnquiry } from '../../Functions/FetchEnquiries';

// Styled components
const EnquiriesContainer = styled.div`
  padding: 1rem;
`;

const FiltersBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 20px;
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
  transition: all 0.2s;
  
  ${({ active, darkMode }) => active && `
    background-color: ${darkMode ? '#2980b9' : '#e6f7ff'};
    color: ${darkMode ? 'white' : '#0077cc'};
    border-color: ${darkMode ? '#3498db' : '#bde0ff'};
    font-weight: 500;
  `}
  
  @media (max-width: 576px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
    flex: 0 1 auto;
  }
`;

const EnquiryCard = styled(motion.div)`
  background: ${props => props.darkMode ? '#2d2d2d' : '#ffffff'};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, ${props => props.darkMode ? '0.25' : '0.1'});
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => {
    if (props.type === 'contact') {
      return props.darkMode ? '#9b59b6' : '#d6a1f9';
    }
    
    switch (props.status) {
      case 'new': return props.darkMode ? '#3498db' : '#bde0ff';
      case 'responded': return props.darkMode ? '#f39c12' : '#ffeaa7';
      case 'completed': return props.darkMode ? '#2ecc71' : '#d4f9e2';
      case 'canceled': return props.darkMode ? '#e74c3c' : '#ffcdd2';
      default: return props.darkMode ? '#95a5a6' : '#e0e0e0';
    }
  }};
`;

const EnquiryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const EnquiryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const EnquiryType = styled.span`
  font-size: 0.85rem;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  
  background-color: ${props => {
    if (props.type === 'contact') {
      return props.darkMode ? '#4a235a' : '#f5e6ff';
    }
    return props.darkMode ? '#1a5276' : '#e6f7ff';
  }};
  
  color: ${props => {
    if (props.type === 'contact') {
      return props.darkMode ? '#d6a1f9' : '#9b59b6';
    }
    return props.darkMode ? '#85c1e9' : '#0077cc';
  }};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
  
  background-color: ${props => {
    switch (props.status) {
      case 'new': return props.darkMode ? '#1a5276' : '#e6f7ff';
      case 'responded': return props.darkMode ? '#7d5d03' : '#fff8e1';
      case 'completed': return props.darkMode ? '#145a32' : '#e5f9ea';
      case 'canceled': return props.darkMode ? '#641e16' : '#ffebee';
      default: return props.darkMode ? '#2c3e50' : '#f5f5f5';
    }
  }};
  
  color: ${props => {
    switch (props.status) {
      case 'new': return props.darkMode ? '#85c1e9' : '#0077cc';
      case 'responded': return props.darkMode ? '#f1c40f' : '#f39c12';
      case 'completed': return props.darkMode ? '#66cc8a' : '#27ae60';
      case 'canceled': return props.darkMode ? '#e57373' : '#e74c3c';
      default: return props.darkMode ? '#b2bec3' : '#7f8c8d';
    }
  }};
`;

const EnquiryDetail = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0.6rem;
  font-size: 0.95rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${props => props.darkMode ? '#bdc3c7' : '#555'};
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const DetailValue = styled.span`
  color: ${props => props.darkMode ? '#f5f5f5' : '#333'};
  margin-right: 1rem;
`;

const DetailSeparator = styled.span`
  margin: 0 0.5rem;
  color: ${props => props.darkMode ? '#555' : '#ddd'};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const EnquiryMessage = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.darkMode ? '#1e1e1e' : '#f9f9f9'};
  border-left: 3px solid ${props => props.darkMode ? '#777' : '#ddd'};
  font-style: italic;
  color: ${props => props.darkMode ? '#ddd' : '#555'};
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 576px) {
    justify-content: stretch;
  }
`;

const NoEnquiriesMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background-color: ${props => props.darkMode ? '#2d2d2d' : '#f9f9f9'};
  border-radius: 8px;
  margin: 2rem 0;
`;

const EnquiriesManagement = ({ 
  bookingEnquiries = [], 
  loadingEnquiries = false, 
  refreshEnquiries,
  darkMode = false 
}) => {
  const [enquiries, setEnquiries] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [deletingEnquiryId, setDeletingEnquiryId] = useState(null); // Track which enquiry is being deleted
  
  // Handle filters based on enquiry type and status
  useEffect(() => {
    if (!bookingEnquiries) return;
    
    let filteredEnquiries = [...bookingEnquiries];
    
    // Filter by type (contact/booking)
    if (activeFilter !== 'all') {
      filteredEnquiries = filteredEnquiries.filter(enquiry => {
        if (activeFilter === 'contact') return enquiry.type === 'contact';
        if (activeFilter === 'booking') return !enquiry.type || enquiry.type !== 'contact';
        return true;
      });
    }
    
    // Filter by status
    if (activeStatus !== 'all') {
      filteredEnquiries = filteredEnquiries.filter(enquiry => enquiry.status === activeStatus);
    }
    
    setEnquiries(filteredEnquiries);
  }, [bookingEnquiries, activeFilter, activeStatus]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get status label with icon
  const getStatusLabel = (status) => {
    switch (status) {
      case 'new':
        return <><FaEnvelope size={12} /> New</>;
      case 'responded':
        return <><FaCheck size={12} /> Responded</>;
      case 'completed':
        return <><FaCheck size={12} /> Completed</>;
      case 'canceled':
        return <><FaTimes size={12} /> Canceled</>;
      default:
        return status;
    }
  };
  
  // Handle status change for enquiry
  const handleStatusChange = async (enquiry, newStatus) => {
    try {
      await updateEnquiryStatus(
        enquiry.year,
        enquiry.month,
        enquiry.date,
        enquiry.id,
        newStatus
      );
      
      toast.success(`Enquiry status updated to ${newStatus}`);
      
      // Refresh enquiries after status change
      if (refreshEnquiries) {
        refreshEnquiries();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update enquiry status");
    }
  };
  
  // Handle enquiry deletion
  const handleDeleteEnquiry = async (enquiry) => {
    if (window.confirm("Are you sure you want to delete this enquiry permanently? This action cannot be undone.")) {
      try {
        setDeletingEnquiryId(enquiry.id); // Set the ID of the enquiry being deleted
        
        // Validate enquiry data
        if (!enquiry.year || !enquiry.month || !enquiry.date || !enquiry.id) {
          throw new Error("Enquiry is missing required data for deletion");
        }
        
        await deleteEnquiry(
          enquiry.year,
          enquiry.month,
          enquiry.date,
          enquiry.id
        );
        
        // Successfully deleted
        toast.success("Enquiry deleted successfully");
        
        // Remove the enquiry from local state immediately for better UX
        setEnquiries(prevEnquiries => prevEnquiries.filter(e => e.id !== enquiry.id));
        
        // Then refresh enquiries from server to ensure data is in sync
        if (refreshEnquiries) {
          refreshEnquiries();
        }
      } catch (error) {
        console.error("Error deleting enquiry:", error);
        toast.error(`Failed to delete enquiry: ${error.message || "Unknown error"}`);
      } finally {
        setDeletingEnquiryId(null); // Reset the deleting state
      }
    }
  };
  
  return (
    <EnquiriesContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="mb-4">Enquiries Management</h2>
        
        <FiltersBar>
          <FilterOptions>
            <span className={`${darkMode ? 'text-light' : 'text-dark'} mb-2 d-block d-md-inline-block`}>
              <FaFilter className="me-2" /> Filter by:
            </span>
            
            {/* Enquiry Type Filters */}
            <div className="d-flex flex-wrap gap-2">
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'}`}
                active={activeFilter === 'all'}
                darkMode={darkMode}
                onClick={() => setActiveFilter('all')}
              >
                All Enquiries
              </FilterButton>
              
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'}`}
                active={activeFilter === 'booking'}
                darkMode={darkMode}
                onClick={() => setActiveFilter('booking')}
              >
                <FaHome size={14} /> Property
              </FilterButton>
              
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'}`}
                active={activeFilter === 'contact'}
                darkMode={darkMode}
                onClick={() => setActiveFilter('contact')}
              >
                <FaEnvelope size={14} /> Contact
              </FilterButton>
            </div>
          </FilterOptions>
          
          <button
            className="btn btn-sm btn-outline-primary w-100 w-md-auto mt-2 mt-md-0"
            onClick={refreshEnquiries}
            disabled={loadingEnquiries}
          >
            <FaSync className={`me-1 ${loadingEnquiries ? 'fa-spin' : ''}`} /> Refresh
          </button>
        </FiltersBar>
        
        {/* Status Filters */}
        <div className="mb-4">
          <FilterOptions>
            <span className={`${darkMode ? 'text-light' : 'text-dark'} mb-2 d-block d-md-inline-block`}>
              Status:
            </span>
            
            <div className="d-flex flex-wrap gap-2">
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'} btn-sm`}
                active={activeStatus === 'all'}
                darkMode={darkMode}
                onClick={() => setActiveStatus('all')}
              >
                All
              </FilterButton>
              
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'} btn-sm`}
                active={activeStatus === 'new'}
                darkMode={darkMode}
                onClick={() => setActiveStatus('new')}
              >
                New
              </FilterButton>
              
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'} btn-sm`}
                active={activeStatus === 'responded'}
                darkMode={darkMode}
                onClick={() => setActiveStatus('responded')}
              >
                Responded
              </FilterButton>
              
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'} btn-sm`}
                active={activeStatus === 'completed'}
                darkMode={darkMode}
                onClick={() => setActiveStatus('completed')}
              >
                Completed
              </FilterButton>
              
              <FilterButton
                className={`btn ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'} btn-sm`}
                active={activeStatus === 'canceled'}
                darkMode={darkMode}
                onClick={() => setActiveStatus('canceled')}
              >
                Canceled
              </FilterButton>
            </div>
          </FilterOptions>
        </div>
        
        {loadingEnquiries ? (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading enquiries...</p>
          </div>
        ) : enquiries.length === 0 ? (
          <NoEnquiriesMessage darkMode={darkMode}>
            <h5 className={darkMode ? 'text-light' : 'text-dark'}>No enquiries found</h5>
            <p className="text-muted">
              {activeFilter !== 'all' || activeStatus !== 'all' 
                ? 'Try changing your filters to see more results' 
                : 'You have no enquiries at the moment'}
            </p>
          </NoEnquiriesMessage>
        ) : (
          <div className="enquiries-list">
            {enquiries.map((enquiry) => (
              <EnquiryCard
                key={enquiry.id}
                darkMode={darkMode}
                status={enquiry.status}
                type={enquiry.type}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <EnquiryHeader>
                  <EnquiryTitle>
                    <h5 className="mb-0">{enquiry.name}</h5>
                    <EnquiryType 
                      type={enquiry.type === 'contact' ? 'contact' : 'booking'} 
                      darkMode={darkMode}
                    >
                      {enquiry.type === 'contact' 
                        ? <><FaEnvelope size={12} /> Contact Form</> 
                        : <><FaHome size={12} /> Property Booking</>}
                    </EnquiryType>
                  </EnquiryTitle>
                  
                  <StatusBadge status={enquiry.status} darkMode={darkMode}>
                    {getStatusLabel(enquiry.status)}
                  </StatusBadge>
                </EnquiryHeader>
                
                {/* Show different details based on enquiry type */}
                {enquiry.type === 'contact' ? (
                  // Contact form enquiry
                  <>
                    <EnquiryDetail>
                      <DetailLabel darkMode={darkMode}>
                        <FaUserAlt size={14} /> Contact:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.email}</DetailValue>
                      <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                      <DetailLabel darkMode={darkMode}>
                        <FaPhoneAlt size={14} /> Phone:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.phone}</DetailValue>
                    </EnquiryDetail>
                    
                    <EnquiryDetail>
                      <DetailLabel darkMode={darkMode}>
                        <FaCalendarAlt size={14} /> Received:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>
                        {formatDate(enquiry.submittedAt || enquiry.enquiryDate)}
                      </DetailValue>
                      <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                      <DetailLabel darkMode={darkMode}>
                        <FaCommentAlt size={14} /> Source:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.source || 'Contact Page'}</DetailValue>
                    </EnquiryDetail>
                    
                    {enquiry.message && (
                      <EnquiryMessage darkMode={darkMode}>
                        "{enquiry.message}"
                      </EnquiryMessage>
                    )}
                  </>
                ) : (
                  // Property booking enquiry
                  <>
                    <EnquiryDetail>
                      <DetailLabel darkMode={darkMode}>
                        <FaHome size={14} /> Property:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.propertyName}</DetailValue>
                      <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                      <DetailLabel darkMode={darkMode}>
                        <FaMapMarkerAlt size={14} /> Location:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.location}</DetailValue>
                    </EnquiryDetail>
                    
                    <EnquiryDetail>
                      <DetailLabel darkMode={darkMode}>
                        <FaCalendarAlt size={14} /> Dates:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>
                        {formatDate(enquiry.checkInDate)} - {formatDate(enquiry.checkOutDate)}
                      </DetailValue>
                      <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                      <DetailLabel darkMode={darkMode}>
                        <FaUserAlt size={14} /> Guests:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>
                        {enquiry.adults} Adults, {enquiry.children || 0} Children
                      </DetailValue>
                    </EnquiryDetail>
                    
                    <EnquiryDetail>
                      <DetailLabel darkMode={darkMode}>
                        <FaUserAlt size={14} /> Contact:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.email}</DetailValue>
                      <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                      <DetailLabel darkMode={darkMode}>
                        <FaPhoneAlt size={14} /> Phone:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{enquiry.phone}</DetailValue>
                    </EnquiryDetail>
                    
                    <EnquiryDetail>
                      <DetailLabel darkMode={darkMode}>
                        <FaCalendarAlt size={14} /> Received:
                      </DetailLabel>
                      <DetailValue darkMode={darkMode}>{formatDate(enquiry.enquiryDate)}</DetailValue>
                    </EnquiryDetail>
                  </>
                )}
                
                <ActionButtons>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleStatusChange(enquiry, 'responded')}
                    disabled={enquiry.status === 'responded' || deletingEnquiryId === enquiry.id}
                  >
                    Mark Responded
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => handleStatusChange(enquiry, 'completed')}
                    disabled={enquiry.status === 'completed' || deletingEnquiryId === enquiry.id}
                  >
                    Mark Completed
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleStatusChange(enquiry, 'canceled')}
                    disabled={enquiry.status === 'canceled' || deletingEnquiryId === enquiry.id}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteEnquiry(enquiry)}
                    title="Delete Enquiry"
                    disabled={deletingEnquiryId === enquiry.id}
                  >
                    {deletingEnquiryId === enquiry.id ? (
                      <><FaSpinner className="fa-spin me-1" /> Deleting...</>
                    ) : (
                      <><FaTrashAlt size={14} /> Delete</>
                    )}
                  </button>
                </ActionButtons>
              </EnquiryCard>
            ))}
          </div>
        )}
      </motion.div>
    </EnquiriesContainer>
  );
};

export default EnquiriesManagement;
